import React, { useMemo } from 'react';
import {
    AreaChart,
    Area,
    ResponsiveContainer,
} from 'recharts';
import { TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type Props = {
    revenueToday: number;
    revenuePrevious: number;
    bookingsToday: number;
    series: any[];
    colors: any;
};

const HomeRevenueCard: React.FC<Props> = ({
    revenueToday,
    revenuePrevious,
    bookingsToday,
    series,
    colors,
}) => {
    const { t, i18n } = useTranslation();
    // Dati mock statici per avere sempre un grafico "ondulato"
    const MOCK_DATA = [
        { value: 15 },
        { value: 25 },
        { value: 18 },
        { value: 40 },
        { value: 22 },
        { value: 35 },
        { value: 30 },
    ];
    const chartData = MOCK_DATA;

    const revenueVariation = useMemo(() => {
        if (!revenuePrevious || revenuePrevious === 0) return 0;
        return ((revenueToday - revenuePrevious) / revenuePrevious) * 100;
    }, [revenueToday, revenuePrevious]);

    return (
        <div className="h-full min-h-[340px]">
            <div className="relative overflow-hidden rounded-3xl shadow-sm bg-white flex flex-col justify-between h-full">
                {/* BG blob */}
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-orange-100 rounded-full blur-3xl opacity-50" />

                <div className="relative z-10 flex-shrink-0 p-6 md:p-8">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-gradient-to-br from-orange-100 to-orange-50 rounded-2xl text-clip-600 shadow-sm">
                            <DollarSign size={24} />
                        </div>
                        {revenueVariation !== 0 && (
                            <span className={`text-sm font-bold px-3 py-1.5 rounded-full flex items-center gap-1 ${revenueVariation > 0 ? 'text-green-700 bg-green-100' : 'text-red-700 bg-red-100'}`}>
                                {revenueVariation > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />} {revenueVariation.toFixed(0)}%
                            </span>
                        )}
                    </div>

                    <h3 className="text-gray-500 font-medium text-sm uppercase tracking-wide">
                        {t('home.revenue_today_title')}
                    </h3>

                    <span className="text-4xl font-bold text-gray-900 block mt-2 tracking-tight">
                        €{' '}
                        {Number(revenueToday || 0).toLocaleString(i18n.language === 'it' ? 'it-IT' : 'en-US', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        })}
                    </span>

                    <p className="text-xs text-gray-400 mt-1">
                        {t('home.bookings_today')}: <b>{bookingsToday}</b>
                    </p>
                </div>

                <div className="h-full min-h-[160px] -mx-4 -mb-4 mt-6 flex-1">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData}>
                            <defs>
                                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                    <stop
                                        offset="5%"
                                        stopColor={colors.primary}
                                        stopOpacity={0.4}
                                    />
                                    <stop
                                        offset="95%"
                                        stopColor={colors.primary}
                                        stopOpacity={0}
                                    />
                                </linearGradient>
                            </defs>
                            <Area
                                type="monotone"
                                dataKey="value"
                                stroke={colors.primary}
                                strokeWidth={3}
                                fillOpacity={1}
                                fill="url(#colorRevenue)"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default HomeRevenueCard;